import EventBus, {
  PRE_EXISTING_FILE_LOADED,
  OPTIONS_UPDATED,
  DIMENSIONS_UPDATED,
  IMAGE_CROPPED,
  MATERIAL_SELECTED,
  SWITCHING_MODE,
} from "./event-bus.js";
import ConfiguratorPreviewController from "./configurator-preview-controller.js?v=9";
import ConfiguratorCropperController from "./configurator-cropper-controller.js?v=14";
import StorageManager from "./wallpaper-configurator-storage-manager.js";
import Utils from "./utils.js";

const INIT_STATE = {
  mode: "",
  originalFile: null,
  processedFile: null,
  originalImageS3Url: null, // S3 url for the original file
  originalImageS3Key: null,
  previewImageS3Url: null, // S3 url for the processed file
  previewImageS3Key: null,
  hasPreExisting: false,
  preExistingUrl: "",
  dimensions: { widthFeet: 0, widthInches: 0, heightFeet: 0, heightInches: 0 },
  hasValidDimensions: false,
  patternSizeInches: 24,
  imageQuality: "No Image",
  imageDPI: 0,
  options: {
    mirror: false,
    blackAndWhite: false,
  },
  selectedMaterial: null,
  selectedVariantId: null,
  squareFootage: 0,
  totalPrice: 0,
  squareFootage: 0,
  initialized: false,
  showPanelDivisions: false,
  showRuler: false,
};

if (!customElements.get("wallpaper-configurator")) {
  class WallpaperConfigurator extends HTMLElement {
    constructor() {
      super();
      console.log("LOG: NEW WallpaperConfigurator constructor");

      this.config = {
        productId: document.querySelector("#wallpaper-configurator")?.dataset.productId,
        minSquareFootage: 0, // no hard size minimum; $120 service charge is the effective floor
        minFileSize: 50 * 1024, // 50KB
        maxFileSize: 1 * 1024 * 1024 * 1024, // 1GB
        // Largest image side accepted (2026-09-17) -- the browser canvas limit
        // (16,384 px; same as MAX_CANVAS_DIMENSION in the PDF path). Past it,
        // generateProcessedPreview() can't draw the image, the preview upload
        // never happens and checkout is blocked with a misleading "Upload
        // failed". Such files get the "File Too Large" bubble at upload instead.
        maxImageSidePx: 16384,
        allowedFileTypes: [
          "image/jpeg",
          "image/jpg",
          "image/png",
          "image/webp",
          "image/tiff",
          "image/tif",
          "image/heic",
          "image/heif",
          "application/pdf",
        ],
        allowedFileExtensions: [
          ".jpg",
          ".jpeg",
          ".png",
          ".webp",
          ".avif",
          ".gif",
          ".bmp",
          ".tiff",
          ".tif",
          ".heic",
          ".heif",
          ".dng",
          ".pdf",
        ],
        heic2anyCDN: "https://cdn.jsdelivr.net/npm/heic2any@0.0.4/dist/heic2any.min.js",
        exifrCDN: "https://cdn.jsdelivr.net/npm/exifr@7.1.3/dist/full.umd.min.js",
        enableS3: true,
      };
      this.objectUrls = [];
      this.state = { ...INIT_STATE };

      // Dependencies
      this.PreviewController = new ConfiguratorPreviewController(this);
      this.CropperController = new ConfiguratorCropperController(this);
      this.StorageManager = new StorageManager(this);
      this.S3 = null;
      if (this.config.enableS3 && window.S3Integration) {
        this.S3 = new S3Integration();
        // console.log("S3 integration initialized");
      }
    }

    connectedCallback() {
      this.selectElements();
      this.attachEventListeners();
      // Initialize only for custom-wallpaper page, mural/coastal will be initialized on click
      if (this.data.template === "product.wallpaper") this.init();

      this.intervalId = setInterval(() => {
        if (document.visibilityState === "visible") {
          this.StorageManager.saveStateToStorage();
        }
      }, 30000);
    }

    async init() {
      this.StorageManager.cleanupOldSessions();
      this.StorageManager.loadStateFromStorage();

      // On the customizer (no forced mode) always start in single mode regardless
      // of what was saved in a previous session -- the user should explicitly choose
      // Pattern mode each visit. Force-mode pages override this below.
      if (!this.data.force_pattern_mode && !this.data.force_mural_mode) {
        this.state.mode = "single";
      }

      console.log("configurator initialized with", this.state);
      const file = await this.getPreExistingFile();
      if (file) {
        this.state.hasPreExisting = true;
        this.state.preExistingUrl = this.data.pattern_url;
        this.state.originalFile = file;
        // On force-pattern / force-mural pages show the product title in the Order
        // Summary "Image" row instead of the generated filename (e.g. "Blue_Floral").
        const forceMode = this.data.force_pattern_mode || this.data.force_mural_mode;
        this.elements.summaryImage.textContent = forceMode
          ? this.data.product?.title || "Image"
          : Utils.getFileNameWithoutExtension(file.name) || "Image";
      }

      this.updateUI();
      this.setDefaultMaterial();
      this.calculateDimensions();
      this.updatePatternSliderConstraints();
      this.updateUploadState();
      this.validateForm();

      if (this.data.force_pattern_mode) {
        this.setMode("pattern");
      } else if (this.data.force_mural_mode) {
        this.setMode("single");
      }

      this.state.initialized = true;
    }

    // Single (mural) mode supports cropping, pattern mode does not
    async setMode(mode = "single") {
      if (this.state.initialized && this.state.mode && (this.data.force_pattern_mode || this.data.force_mural_mode)) {
        console.error("Mode is forced, no mode switching");
        return;
      }

      this.state.mode = mode;
      console.log("set mode", this.state);
      if (!this.state.originalFile && !this.state.processedFile) return;

      switch (mode) {
        case "pattern":
          this.prepareUIForPatternMode();

          if (this.state.hasPreExisting) {
            EventBus.emit(PRE_EXISTING_FILE_LOADED, { file: this.state.originalFile, mode });
            break;
          }

          EventBus.emit(SWITCHING_MODE, { file: this.state.processedFile ?? this.state.originalFile, mode });

          break;

        case "single":
          this.prepareUIForSingleMode();

          if (this.state.hasPreExisting) {
            EventBus.emit(PRE_EXISTING_FILE_LOADED, { file: this.state.originalFile, mode });
            break;
          }

          EventBus.emit(SWITCHING_MODE, { file: this.state.processedFile ?? this.state.originalFile, mode });

          break;
      }
    }

    async getPreExistingFile() {
      const _url = this.data?.pattern_url;
      if (!_url) {
        console.log("No pre-existing pattern URL found");
        return;
      }

      let url = _url;
      if (url.startsWith("//")) url = "https:" + url;
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Failed to load image: ${response.status} ${response.statusText}`);

        const blob = await response.blob();
        const { fileExtension, fileType } = Utils.getFileInfo(blob);
        const filename = this.data.product.title
          ? `${this.data.product.title.replace(/\s+/g, "_")}.${fileExtension}`
          : `pattern.${fileExtension}`;
        const file = new File([blob], filename, { type: fileType });
        console.log("LOG: FILE", file);

        return file;
      } catch (error) {
        console.error("ERROR: Failed to get pre-existing file", error);
      }
    }

    selectElements() {
      this.elements = {
        root: this.querySelector("#wallpaper-configurator"),

        bottomCardsContainer: this.querySelector(".bottom-cards-container"),

        widthFeet: this.querySelector("#width-feet") ?? this.querySelector("#input-width-feet"),
        widthInch: this.querySelector("#width-inches") ?? this.querySelector("#input-width-inch"),
        heightFeet: this.querySelector("#height-feet") ?? this.querySelector("#input-height-feet"),
        heightInch: this.querySelector("#height-inches") ?? this.querySelector("#input-height-inch"),
        // Material
        materialSelect: this.querySelector("#material-select"),
        // Upload
        imageUpload: this.querySelector("#image-upload"),
        uploadZone: this.querySelector("#upload-zone"),
        previewArea: this.querySelector("#preview-area"),
        previewImage: this.querySelector("#preview-image"),
        patternCanvas: this.querySelector("#pattern-canvas"),
        clearImageBtn: this.querySelector("#clear-image"),
        recropBtn: this.querySelector("#recrop-image"),
        rotateBtn: this.querySelector("#rotate-image"),
        // Cropping
        croppingArea: this.querySelector("#cropping-area"),
        cropImage: this.querySelector("#crop-image"),
        cancelCropBtn: this.querySelector("#cancel-crop"),
        applyCropBtn: this.querySelector("#apply-crop"),
        // Mode Toggle (single button)
        modePatternBtn: this.querySelector("#mode-pattern-btn"),
        modeToggleControls: this.querySelector(".mode-toggle-controls"),
        // Options
        mirrorBtn: this.querySelector("#mirror-btn"),
        bwBtn: this.querySelector("#bw-btn"),
        resetAllBtn: this.querySelector("#reset-all-btn"),
        mirrorInput: this.querySelector("#mirror-input"),
        bwInput: this.querySelector("#black-and-white-input"),
        // Summary
        summaryMaterial: this.querySelector("#summary-material"),
        summaryDimensions: this.querySelector("#summary-dimensions"),
        summaryArea: this.querySelector("#summary-area"),
        summaryImage: this.querySelector("#summary-image"),
        summaryPrice: this.querySelector("#summary-price"),
        // Quality
        qualityDPI: this.querySelector("#quality-dpi"),
        qualityScore: this.querySelector("#quality-score"),
        // Cart
        addToCartBtn: this.querySelector("#add-to-cart-button"),
        // Pattern
        patternSettings: this.querySelector("#pattern-settings"),
        patternSizeSlider: this.querySelector("#pattern-size-slider"),
        patternSizeValue: this.querySelector("#pattern-size-value"),
        // Hidden inputs
        squareFootageInput: this.querySelector("#input-square-footage"),
        calculatedPriceInput: this.querySelector("#input-calculated-price"),
        // Toggle controls
        togglePanelDivisions: this.querySelector("#toggle-panel-divisions"),
        toggleRuler: this.querySelector("#toggle-ruler"),
        // Mobile tool bar (mirrors panel + ruler for mobile layout -- rotate now lives in unified .tools-bar)
        mobileTogglePanelDivisions: this.querySelector("#mobile-toggle-panel-divisions"),
        mobileToggleRuler: this.querySelector("#mobile-toggle-ruler"),
      };

      try {
        this.data = JSON.parse(this.querySelector("#data").textContent);
        console.log("LOG: DATA", this.data);
      } catch (error) {
        this.data = {};
        console.error("ERROR: Failed to parse JSON data", error);
      }
    }

    // ==== HELPERS - START ====

    setDefaultMaterial() {
      const select = this.elements.materialSelect;
      if (!select) return;

      // Check if a variant was pre-selected on the product page
      const savedVariantId = sessionStorage.getItem(`configurator_variant_${this.config.productId}`);
      if (savedVariantId) {
        const matchingOption = Array.from(select.options).find((o) => o.value === savedVariantId);
        if (matchingOption) {
          select.value = savedVariantId;
          this.state.selectedVariantId = savedVariantId;
          sessionStorage.removeItem(`configurator_variant_${this.config.productId}`);
        }
      }

      const selectedOption = select.options[select.selectedIndex];
      if (selectedOption && selectedOption.value) {
        this.state.selectedMaterial = {
          id: selectedOption.value,
          name: selectedOption.textContent,
          price: parseFloat(selectedOption.dataset.price) || 0,
        };
        this.updateMaterialDisplay();
        this.calculatePrice();
      }
    }

    updatePatternSizeDisplay() {
      if (!this.elements.patternSizeValue) return;

      const basePatternSize = 24; // Default pattern size
      const sizeInches = this.state.patternSizeInches;
      const percentage = Math.round((sizeInches / basePatternSize) * 100);

      // Update display to show both inches and scale
      if (percentage === 100) {
        this.elements.patternSizeValue.textContent = `${sizeInches}" (Original)`;
      } else if (percentage < 100) {
        this.elements.patternSizeValue.textContent = `${sizeInches}" (${percentage}% scale)`;
      } else {
        const multiplier = (sizeInches / basePatternSize).toFixed(1);
        this.elements.patternSizeValue.textContent = `${sizeInches}" (${multiplier}x size)`;
      }
    }

    updateMaterialDisplay() {
      this.elements.materialSelect.value = this.state.selectedVariantId ?? "";
      if (this.elements.summaryMaterial) {
        this.elements.summaryMaterial.textContent = this.state.selectedMaterial?.name || "Select a Material";
      }
      // Keep visible material list in sync with the hidden select
      this.syncMaterialList(this.state.selectedVariantId);
    }

    // Sync active class on the visible material-list rows.
    // Called after any programmatic or user-triggered material selection.
    syncMaterialList(variantId) {
      const variantStr = String(variantId ?? "");
      this.querySelectorAll(".material-item").forEach((item) => {
        const active = item.dataset.variantId === variantStr;
        item.classList.toggle("active", active);
        item.setAttribute("aria-selected", active ? "true" : "false");
      });
    }

    updateImageQuality() {
      // Recalculate DPI based on new dimensions if we have an image
      if (!this.state.processedFile && !this.state.originalFile) return;

      // Create a temporary image object to recalculate
      if (this.state.originalImageDimensions) {
        // Use a mock image object with the stored dimensions
        const mockImg = {
          naturalWidth: this.state.originalImageDimensions.width,
          naturalHeight: this.state.originalImageDimensions.height,
        };
        // Call the main calculation method with our mock image
        this.calculateImageQualityFromImg(mockImg);
      }
    }

    calculatePrice() {
      if (!this.state.selectedMaterial || this.state.squareFootage <= 0) {
        this.state.totalPrice = 0;
      } else {
        // Price is now actual price per sq ft (e.g., $7.00 = $7/sq ft)
        const pricePerSqFt = parseFloat(this.state.selectedMaterial.price) || 0;
        this.state.totalPrice = this.state.squareFootage * pricePerSqFt;

        // Enforce $120 minimum service charge (billed as whole sq-ft units)
        if (pricePerSqFt > 0 && this.state.totalPrice > 0) {
          const minCharge = Math.ceil(120 / pricePerSqFt) * pricePerSqFt;
          if (this.state.totalPrice < minCharge) this.state.totalPrice = minCharge;
        }

        // Debug logging
        // console.log("Price calculation:", {
        //   squareFootage: this.state.squareFootage,
        //   pricePerSqFt: pricePerSqFt,
        //   materialPrice: this.state.selectedMaterial.price,
        //   totalPrice: this.state.totalPrice,
        // });
      }

      // Update hidden input
      if (this.elements.calculatedPriceInput) {
        this.elements.calculatedPriceInput.value = this.state.totalPrice.toFixed(2);
      }

      // Update display
      if (this.elements.summaryPrice) {
        this.elements.summaryPrice.textContent = `$${this.state.totalPrice.toFixed(2)}`;
      }
    }

    validateForm() {
      // Allow adding to cart as long as we have all three components
      // Dimensions can be set after image upload
      const hasValidDimensions = this.state.squareFootage > 0;
      const hasMaterial = this.state.selectedMaterial !== null;
      const hasImage = this.state.processedFile !== null || this.state.originalFile !== null;

      // All three are still required for cart, but order doesn't matter
      const isValid = hasValidDimensions && hasMaterial && hasImage;

      if (this.elements.addToCartBtn) {
        this.elements.addToCartBtn.disabled = !isValid;
      }
    }

    calculateDimensions() {
      const widthFeet = parseFloat(this.elements.widthFeet?.value) || 0;
      const widthInches = parseFloat(this.elements.widthInch?.value) || 0;
      const heightFeet = parseFloat(this.elements.heightFeet?.value) || 0;
      const heightInches = parseFloat(this.elements.heightInch?.value) || 0;

      const totalWidthInches = widthFeet * 12 + widthInches;
      const totalHeightInches = heightFeet * 12 + heightInches;

      // Maximum dimensions check (999 ft = 11,988 inches max per side)
      const MAX_INCHES = 12000; // ~1000 feet
      if (totalWidthInches > MAX_INCHES) {
        alert("Maximum width is 1000 feet");
        this.elements.widthFeet.value = 999;
        this.elements.widthInch.value = 0;
        return;
      }
      if (totalHeightInches > MAX_INCHES) {
        alert("Maximum height is 1000 feet");
        this.elements.heightFeet.value = 999;
        this.elements.heightInch.value = 0;
        return;
      }

      // Maximum total area check to prevent browser crashes
      const squareFeet = (totalWidthInches * totalHeightInches) / 144;
      const MAX_SQUARE_FEET = 100000; // 100,000 sq ft max
      if (squareFeet > MAX_SQUARE_FEET) {
        alert(
          `Maximum total area is ${MAX_SQUARE_FEET.toLocaleString()} square feet. Current: ${Math.round(squareFeet).toLocaleString()} sq ft`,
        );
        return;
      }

      this.state.dimensions = { widthFeet, widthInches, heightFeet, heightInches };
      this.state.squareFootage = (totalWidthInches * totalHeightInches) / 144;
      // Check if dimensions are valid (minimum 12 sq ft)
      this.state.hasValidDimensions = this.state.squareFootage > 0;

      // Unlock drag-to-pan once dimensions are valid (and re-lock if they
      // go invalid). Applies to both mural and customizer uploads.
      if (this.state.cropper) {
        this.state.cropper.setDragMode(this.state.hasValidDimensions ? "move" : "none");
      }

      // Update hidden input
      if (this.elements.squareFootageInput) {
        this.elements.squareFootageInput.value = this.state.squareFootage.toFixed(2);
      }

      // // Enable/disable upload based on dimensions
      // this.UIController.updateUploadState();
      //
      // this.UIController.updateDimensionDisplay();
      // this.calculatePrice();
      // this.validateForm();
      //
      // // Update pattern slider range when dimensions change
      // this.UIController.updatePatternSliderRange();
      // this.UIController.updatePatternSizeDisplay();

      //TODO: Everything above handled, the following will be handled thru events
      //TODO: pattern mode path handled
      return;
      // Update cropper aspect ratio if cropper exists
      if (this.state.cropper) {
        this.CanvasController.updateCropperAspectRatio();
      }

      // Update display based on current mode
      if (this.state.uploadedImage && this.state.squareFootage > 0) {
        if (this.state.imageMode === "pattern") {
          this.CanvasController.updatePatternPreview();
        } else if (this.state.imageMode === "single") {
          // For mural mode, update the display to show dimensions
          this.CanvasController.updateMuralDisplay();
        }
      }

      // Update pattern slider constraints based on new dimensions
      this.UIController.updatePatternSliderConstraints();

      // Update the preview if we have an image
      if (this.state.uploadedImage || this.state.originalFile) {
        if (this.state.imageMode === "pattern") {
          // Re-render pattern with new dimensions
          this.CanvasController.updatePatternPreview();
        } else {
          // For full image mode, update the aspect ratio of the display
          // The cropped image itself doesn't change, but we may want to show dimension info
          this.CanvasController.updateImageQuality();

          // Update cropper aspect ratio if we're currently cropping
          if (this.state.cropper && this.elements.croppingArea.style.display !== "none") {
            const totalWidthInches = this.state.dimensions.widthFeet * 12 + this.state.dimensions.widthInches;
            const totalHeightInches = this.state.dimensions.heightFeet * 12 + this.state.dimensions.heightInches;

            if (totalWidthInches && totalHeightInches) {
              const newAspectRatio = totalWidthInches / totalHeightInches;
              // console.log("Updating cropper aspect ratio to:", newAspectRatio);

              // Update the cropper's aspect ratio
              this.state.cropper.setAspectRatio(newAspectRatio);

              // Reset crop box to full size with new aspect ratio
              this.state.cropper.reset();
              this.state.cropper.setCropBoxData({
                left: 0,
                top: 0,
                width: this.state.cropper.getContainerData().width,
                height: this.state.cropper.getContainerData().width / newAspectRatio,
              });
            }
          }
        }
      }

      // Save state to localStorage
      this.StorageManager.saveStateToStorage();
    }

    updateUploadState() {
      if (!this.elements.uploadZone) return;

      // Always enable upload - no dimension gate
      this.elements.uploadZone.classList.remove("disabled");
      if (this.elements.imageUpload) {
        this.elements.imageUpload.disabled = false;
      }

      // Keep upload text consistent
      const uploadText = this.elements.uploadZone.querySelector(".upload-text");
      if (uploadText) {
        uploadText.textContent = "Click or Drag & Drop to Upload Image";
      }

      // Also disable/enable More Options based on image
      if (this.elements.patternBtn && this.elements.mirrorBtn && this.elements.bwBtn) {
        const hasImage = this.state.processedFile !== null || this.state.originalFile !== null;
        this.elements.patternBtn.disabled = !hasImage;
        this.elements.mirrorBtn.disabled = !hasImage;
        this.elements.bwBtn.disabled = !hasImage;

        // Add visual feedback
        [this.elements.patternBtn, this.elements.mirrorBtn, this.elements.bwBtn].forEach((btn) => {
          btn.classList.toggle("disabled", !hasImage);
        });
      }
    }

    updateDimensionsSummary() {
      const { widthFeet, widthInches, heightFeet, heightInches } = this.state.dimensions;

      let widthStr = widthFeet > 0 ? `${widthFeet}'` : "";
      if (widthInches > 0 || widthFeet === 0) {
        widthStr += `${widthInches.toFixed(1)}"`;
      }
      if (!widthStr) widthStr = `0.0"`;

      let heightStr = heightFeet > 0 ? `${heightFeet}'` : "";
      if (heightInches > 0 || heightFeet === 0) {
        heightStr += `${heightInches.toFixed(1)}"`;
      }
      if (!heightStr) heightStr = `0.0"`;

      if (this.elements.summaryDimensions) {
        this.elements.summaryDimensions.textContent = `${widthStr} \u00d7 ${heightStr}`;
      }

      if (this.elements.summaryArea) {
        this.elements.summaryArea.textContent = `${this.state.squareFootage.toFixed(2)} sq ft`;
      }
    }

    updatePatternSliderRange() {
      // Get wall width (the limiting dimension for pattern size)
      const wallWidthInches = this.state.dimensions.widthFeet * 12 + this.state.dimensions.widthInches;

      if (wallWidthInches > 0 && this.elements.patternSizeSlider) {
        // Max pattern size is either wall width or 144" (12 feet), whichever is smaller
        // This prevents patterns larger than the wall itself
        const maxPatternSize = Math.min(wallWidthInches, 144);

        // Update slider max attribute
        this.elements.patternSizeSlider.max = maxPatternSize;

        // If current value exceeds new max, adjust it
        if (this.state.patternSizeInches > maxPatternSize) {
          this.state.patternSizeInches = maxPatternSize;
          this.elements.patternSizeSlider.value = maxPatternSize;
          this.updatePatternSizeDisplay();
        }

        // console.log("Updated pattern slider range:", {
        //   wallWidth: wallWidthInches,
        //   maxPattern: maxPatternSize,
        //   currentPattern: this.configurator.state.patternSizeInches,
        // });
      }
    }

    updatePatternSliderConstraints() {
      if (!this.elements.patternSizeSlider) return;
      const { widthFeet, widthInches, heightFeet, heightInches } = this.state.dimensions;

      // Get wall dimensions in inches
      const wallWidthInches = widthFeet * 12 + widthInches;
      const wallHeightInches = heightFeet * 12 + heightInches;

      // Find the smallest dimension
      const smallestDimension = Math.min(wallWidthInches || 96, wallHeightInches || 72);

      // Set min to 12 inches, max to the smallest wall dimension (capped at 96 inches / 8 feet)
      const minSize = 12;
      const absoluteMax = 96; // 8 feet maximum pattern size
      const maxSize = Math.min(absoluteMax, Math.max(minSize, smallestDimension));

      // Update slider attributes
      this.elements.patternSizeSlider.min = minSize;
      this.elements.patternSizeSlider.max = maxSize;

      // Ensure current value is within bounds
      if (this.state.patternSizeInches < minSize) {
        this.state.patternSizeInches = minSize;
        this.elements.patternSizeSlider.value = minSize;
        if (this.elements.patternSizeValue) {
          this.updatePatternSizeDisplay();
        }
      } else if (this.state.patternSizeInches > maxSize) {
        this.state.patternSizeInches = maxSize;
        this.elements.patternSizeSlider.value = maxSize;
        if (this.elements.patternSizeValue) {
          this.updatePatternSizeDisplay();
        }
      }
    }

    updateModeUI() {
      if (this.state.mode === "pattern") {
        this.elements.modePatternBtn?.classList.add("active");
        this.elements.modePatternBtn?.classList.add("utility-btn--active");
      } else {
        this.elements.modePatternBtn?.classList.remove("active");
        this.elements.modePatternBtn?.classList.remove("utility-btn--active");
      }
      // Panels/Ruler cards are always visible regardless of mode -- they mirror
      // the Mural editor layout so users can toggle panel lines and the ruler
      // overlay in Pattern mode too.

      // Keep aria-expanded on the Pattern button in sync with pattern mode so
      // screen readers announce the popover (controlled via .active + CSS :has())
      // as expanded/collapsed. Optional chaining: element is absent on pattern-only
      // and mural templates.
      this.elements.modePatternBtn?.setAttribute(
        "aria-expanded",
        this.state.mode === "pattern" ? "true" : "false"
      );

      // Pattern slider row is always visible in the .tools-bar -- toggle enabled/disabled
      // state instead of hiding it. Class drives the visual greyed-out look;
      // `disabled` attr blocks interaction.
      if (this.elements.patternSettings) {
        const active = this.state.mode === "pattern";
        this.elements.patternSettings.classList.toggle("is-disabled", !active);
        if (this.elements.patternSizeSlider) this.elements.patternSizeSlider.disabled = !active;
      }
    }

    updateDimensionDisplay() {
      const { widthFeet, widthInches, heightFeet, heightInches } = this.state.dimensions;

      this.elements.heightFeet.value = heightFeet;
      this.elements.heightInch.value = heightInches;
      this.elements.widthFeet.value = widthFeet;
      this.elements.widthInch.value = widthInches;

      let widthStr = widthFeet > 0 ? `${widthFeet}'` : "";
      if (widthInches > 0 || widthFeet === 0) {
        widthStr += `${widthInches.toFixed(1)}"`;
      }
      if (!widthStr) widthStr = `0.0"`;

      let heightStr = heightFeet > 0 ? `${heightFeet}'` : "";
      if (heightInches > 0 || heightFeet === 0) {
        heightStr += `${heightInches.toFixed(1)}"`;
      }
      if (!heightStr) heightStr = `0.0"`;

      if (this.elements.summaryDimensions) {
        this.elements.summaryDimensions.textContent = `${widthStr} \u00d7 ${heightStr}`;
      }

      if (this.elements.summaryArea) {
        this.elements.summaryArea.textContent = `${this.state.squareFootage.toFixed(2)} sq ft`;
      }
    }

    updateUploadStatus(message) {
      const statusEl = this.querySelector("#upload-status");
      if (statusEl) {
        statusEl.textContent = message;
      }
      this.setEditorLoaderStage(message); // no-op unless the loading bar is up
    }

    updateUI() {
      this.updateDimensionDisplay();
      this.updateMaterialDisplay();
      this.updateQualityDisplay();
      this.updateModeUI();
      this.calculatePrice();
      this.validateForm();

      this.elements.modeSingleBtn?.classList.toggle("active", this.state.mode === "single");
      this.elements.modePatternBtn?.classList.toggle("active", this.state.mode === "pattern");

      this.elements.mirrorBtn?.classList.toggle("active", this.state.options.mirror);
      this.elements.bwBtn?.classList.toggle("active", this.state.options.blackAndWhite);
    }

    validateFile(file) {
      if (!file) return false;
      const type = (file.type || "").toLowerCase();
      const name = (file.name || "").toLowerCase();
      const byMime = this.config.allowedFileTypes.includes(type);
      const byExt = this.config.allowedFileExtensions.some((ext) => name.endsWith(ext));
      const byImagePrefix = type.startsWith("image/");
      const byPdf = type === "application/pdf";
      if (!byMime && !byExt && !byImagePrefix && !byPdf) {
        const detected = `(Detected: type="${file.type || "unknown"}", name="${file.name || "unknown"}")`;
        alert("Please upload a JPG, PNG, HEIC, WebP, TIFF, or PDF file.\n\n" + detected);
        console.warn("validateFile rejected:", { type: file.type, name: file.name, size: file.size });
        return false;
      }

      if (file.size < this.config.minFileSize) {
        alert("File size must be at least 50KB.");
        return false;
      }

      if (file.size > this.config.maxFileSize) {
        const gb = (file.size / (1024 * 1024 * 1024)).toFixed(1);
        this.showFileTooLarge(`This file is ${gb} GB. Our online editor accepts files up to 1 GB.`);
        return false;
      }

      return true;
    }

    // ---- "File Too Large" bubble (2026-09-17) ----
    // Same component and copy as custom-sample-editor.liquid's
    // #cse-file-too-large; styles in wallpaper-configurator-v2.css.

    isTIFFUpload(file) {
      const type = (file.type || "").toLowerCase();
      return type === "image/tiff" || type === "image/tif" || /\.(tiff|tif)$/i.test(file.name || "");
    }

    // Pixel size without drawing anything (TIFF from its header via
    // UTIF.decode, which parses tags only). PDFs are skipped: their preview is
    // rendered through an already size-capped canvas. Returns null when the
    // size can't be read.
    async readPixelSize(file) {
      const isPdf = (file.type || "").toLowerCase() === "application/pdf" || /\.pdf$/i.test(file.name || "");
      if (isPdf) return null;
      if (this.isTIFFUpload(file)) {
        if (typeof UTIF === "undefined") return null;
        try {
          const ifd = UTIF.decode(await file.arrayBuffer())?.[0];
          return ifd && ifd.t256 && ifd.t257 ? { width: ifd.t256[0], height: ifd.t257[0] } : null;
        } catch (e) {
          return null;
        }
      }
      const url = URL.createObjectURL(file);
      try {
        return await new Promise((resolve) => {
          const img = new Image();
          img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
          img.onerror = () => resolve(null);
          img.src = url;
        });
      } finally {
        URL.revokeObjectURL(url);
      }
    }

    async isOverPixelLimit(file) {
      const size = await this.readPixelSize(file);
      const max = this.config.maxImageSidePx;
      if (!size || Math.max(size.width, size.height) <= max) return false;
      const fmt = (n) => Number(n).toLocaleString("en-US");
      this.showFileTooLarge(
        `This image is ${fmt(size.width)} × ${fmt(size.height)} px. Our online editor handles up to ${fmt(max)} px per side.`
      );
      return true;
    }

    showFileTooLarge(detail) {
      const zone = this.elements.uploadZone;
      if (!zone) {
        alert("File Too Large. " + detail); // never fail silently
        return;
      }
      let box = zone.querySelector(".ac-file-too-large");
      if (!box) {
        box = document.createElement("div");
        box.className = "ac-file-too-large";
        box.setAttribute("role", "alert");
        box.innerHTML =
          '<strong class="ac-file-too-large__title">File Too Large</strong>' +
          '<p class="ac-file-too-large__body">Large-format files are handled through our Trade Program.</p>' +
          '<p class="ac-file-too-large__detail"></p>' +
          '<a class="ac-file-too-large__link" href="/pages/wallpaper-trade-program" target="_blank" rel="noopener">Explore the Trade Program &rarr;</a>';
        // #upload-zone opens the file picker on click; keep clicks in the bubble (incl. the link) from doing that.
        box.addEventListener("click", (e) => e.stopPropagation());
        zone.appendChild(box);
      }
      box.querySelector(".ac-file-too-large__detail").textContent = detail || "";
      box.hidden = false;
      const r = box.getBoundingClientRect();
      if (r.top < 0 || r.bottom > window.innerHeight) box.scrollIntoView({ behavior: "smooth", block: "center" });
    }

    hideFileTooLarge() {
      const box = this.elements.uploadZone?.querySelector(".ac-file-too-large");
      if (box) box.hidden = true;
    }

    // ---- loading bar (2026-09-17) ----
    // Same component as custom-sample-editor.js (.ac-editor-loader styles in
    // wallpaper-configurator-v2.css). Covers .upload-canvas from file pick
    // until the cropper fires "ready" (single mode) or the preview controller
    // finishes (pattern mode / any other exit). Big or slow formats show it at
    // once; small photos only if still loading after 300 ms.
    queueEditorLoader(file) {
      clearTimeout(this._loaderTimer);
      const name = file.name || "";
      const type = (file.type || "").toLowerCase();
      const isPdf = type === "application/pdf" || /\.pdf$/i.test(name);
      const slow = file.size > 15 * 1024 * 1024 || isPdf || this.isTIFFUpload(file) || this.isHEICFile(file) || this.isDNGFile(file);
      const stage = isPdf || this.isTIFFUpload(file) ? "Reading file…" : "Loading image…";
      if (slow) this.showEditorLoader(stage);
      else this._loaderTimer = setTimeout(() => this.showEditorLoader(stage), 300);
    }

    showEditorLoader(stage, progress) {
      const host = this.elements.uploadZone?.parentElement; // .upload-canvas
      if (!host) return;
      let el = host.querySelector(":scope > .ac-editor-loader");
      if (!el) {
        el = document.createElement("div");
        el.className = "ac-editor-loader";
        el.setAttribute("role", "status");
        el.setAttribute("aria-live", "polite");
        el.innerHTML =
          '<div class="ac-editor-loader__card">' +
          '<strong class="ac-editor-loader__title">Loading your file</strong>' +
          '<span class="ac-editor-loader__stage"></span>' +
          '<div class="ac-editor-loader__track"><div class="ac-editor-loader__bar"></div></div>' +
          '<span class="ac-editor-loader__note">Large files can take a little while.</span>' +
          "</div>";
        // Swallow clicks so the dropzone underneath doesn't open the file picker.
        el.addEventListener("click", (e) => e.stopPropagation());
        host.appendChild(el);
      }
      host.classList.add("is-editor-loading");
      el.hidden = false;
      this.setEditorLoaderStage(stage, progress);
    }

    // progress: 0..1 for a real percentage, anything else = indeterminate.
    setEditorLoaderStage(stage, progress) {
      const el = this.elements.uploadZone?.parentElement?.querySelector(":scope > .ac-editor-loader");
      if (!el || el.hidden) return;
      const hasPct = typeof progress === "number" && isFinite(progress);
      if (stage) {
        const label = String(stage).replace(/\.\.\.$/, "…");
        el.querySelector(".ac-editor-loader__stage").textContent = hasPct ? `${label} ${Math.round(progress * 100)}%` : label;
      }
      el.querySelector(".ac-editor-loader__track").classList.toggle("is-indeterminate", !hasPct);
      el.querySelector(".ac-editor-loader__bar").style.width = hasPct ? `${Math.max(2, Math.round(progress * 100))}%` : "";
    }

    hideEditorLoader() {
      clearTimeout(this._loaderTimer);
      clearTimeout(this._loaderWatchdog);
      const host = this.elements.uploadZone?.parentElement;
      if (!host) return;
      host.classList.remove("is-editor-loading");
      const el = host.querySelector(":scope > .ac-editor-loader");
      if (el) el.hidden = true;
    }

    isEditorLoaderUp() {
      const host = this.elements.uploadZone?.parentElement;
      return !!host && host.classList.contains("is-editor-loading");
    }

    // setMode() can return without handing the file to the preview controller
    // (e.g. mode is forced on pattern/mural pages); don't leave the bar up then.
    async startPreviewWithLoader(run) {
      const before = this._previewRuns || 0;
      await run();
      if ((this._previewRuns || 0) === before) this.hideEditorLoader();
    }

    // Called by ConfiguratorPreviewController.processFile() on every exit.
    onPreviewProcessed() {
      // Note: the bar may not be up yet (small files show it after 300 ms);
      // hideEditorLoader() below also cancels that pending show.
      const cropper = this.state.cropper;
      const cropping = this.elements.croppingArea && this.elements.croppingArea.style.display !== "none";
      const imgPending = !!this.elements.cropImage?.getAttribute("src");
      // The PDF/TIFF branches don't await showCroppingInterface(), so the
      // image may still be decoding (no cropper yet) -- that decode is the
      // "bare thumbnail" wait. Keep the bar up until the crop-image
      // "ready"/"error" listeners clear it.
      if (this.state.mode === "single" && cropping && imgPending && !(cropper && cropper.ready)) {
        this.setEditorLoaderStage("Opening editor…"); // no-op if not shown yet
        clearTimeout(this._loaderWatchdog);
        this._loaderWatchdog = setTimeout(() => this.hideEditorLoader(), 90000); // never cover the page forever
        return;
      }
      this.hideEditorLoader();
    }

    nextPaint() {
      // rAF doesn't run in a background tab, so never wait on it alone.
      return new Promise((resolve) => {
        const done = () => { clearTimeout(t); resolve(); };
        const t = setTimeout(resolve, 60);
        requestAnimationFrame(() => setTimeout(done, 0));
      });
    }

    readFileWithProgress(file, onProgress) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onprogress = (e) => {
          if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
        };
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error || new Error("File read failed"));
        reader.readAsArrayBuffer(file);
      });
    }

    isHEICFile(file) {
      if (!file) return false;
      const type = (file.type || "").toLowerCase();
      const name = (file.name || "").toLowerCase();
      return type === "image/heic" || type === "image/heif" || /\.(heic|heif)$/.test(name);
    }

    isDNGFile(file) {
      if (!file) return false;
      const type = (file.type || "").toLowerCase();
      const name = (file.name || "").toLowerCase();
      return type === "image/x-adobe-dng" || type === "image/dng" || /\.dng$/.test(name);
    }

    isVideoFile(file) {
      if (!file) return false;
      const type = (file.type || "").toLowerCase();
      const name = (file.name || "").toLowerCase();
      return type.startsWith("video/") || /\.(mov|mp4|m4v|avi|mkv|webm)$/.test(name);
    }

    async extractDNGPreview(file) {
      // iPhone ProRAW DNGs contain a full-res JPEG preview stored in sensor
      // orientation (so portrait iPhone shots come out as sideways landscape).
      // We: (1) scan bytes for the largest embedded JPEG; (2) read the TIFF
      // IFD Orientation tag from the DNG header; (3) re-render the JPEG
      // through a canvas with the correct rotation applied.
      const arrayBuffer = await file.arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);
      const jpegs = [];
      let i = 0;
      while (i < bytes.length - 3) {
        if (bytes[i] === 0xff && bytes[i + 1] === 0xd8 && bytes[i + 2] === 0xff) {
          const start = i;
          let found = false;
          for (let j = start + 2; j < bytes.length - 1; j++) {
            if (bytes[j] === 0xff && bytes[j + 1] === 0xd9) {
              const end = j + 2;
              jpegs.push({ start, end, size: end - start });
              i = end;
              found = true;
              break;
            }
          }
          if (!found) break;
        } else {
          i++;
        }
      }
      if (jpegs.length === 0) throw new Error("No JPEG preview found inside DNG file");
      const largest = jpegs.reduce((a, b) => (b.size > a.size ? b : a));
      const jpegBytes = bytes.slice(largest.start, largest.end);

      const orientation = this.readDNGOrientation(bytes);
      const baseName = (file.name || "raw.dng").replace(/\.dng$/i, ".jpg");

      if (!orientation || orientation === 1) {
        return new File([jpegBytes], baseName, { type: "image/jpeg" });
      }

      try {
        const rotatedBlob = await this.applyOrientationToJpeg(jpegBytes, orientation);
        return new File([rotatedBlob], baseName, { type: "image/jpeg" });
      } catch (err) {
        console.warn("DNG orientation correction failed, using raw preview:", err);
        return new File([jpegBytes], baseName, { type: "image/jpeg" });
      }
    }

    readDNGOrientation(bytes) {
      // DNGs start with a TIFF header. Parse IFD 0 to find Orientation (tag 0x0112).
      if (bytes.length < 8) return 1;
      const byteOrder = String.fromCharCode(bytes[0], bytes[1]);
      if (byteOrder !== "II" && byteOrder !== "MM") return 1;
      const le = byteOrder === "II";
      const read16 = (o) =>
        le ? bytes[o] | (bytes[o + 1] << 8) : (bytes[o] << 8) | bytes[o + 1];
      const read32 = (o) =>
        le
          ? (bytes[o] | (bytes[o + 1] << 8) | (bytes[o + 2] << 16) | (bytes[o + 3] << 24)) >>> 0
          : ((bytes[o] << 24) | (bytes[o + 1] << 16) | (bytes[o + 2] << 8) | bytes[o + 3]) >>> 0;
      if (read16(2) !== 42) return 1;
      const ifdOffset = read32(4);
      if (ifdOffset + 2 > bytes.length) return 1;
      const numEntries = read16(ifdOffset);
      for (let k = 0; k < numEntries; k++) {
        const entryOffset = ifdOffset + 2 + k * 12;
        if (entryOffset + 12 > bytes.length) break;
        const tag = read16(entryOffset);
        if (tag === 0x0112) {
          return read16(entryOffset + 8);
        }
      }
      return 1;
    }

    async applyOrientationToJpeg(jpegBytes, orientation) {
      const srcBlob = new Blob([jpegBytes], { type: "image/jpeg" });
      const url = URL.createObjectURL(srcBlob);
      try {
        const img = await new Promise((resolve, reject) => {
          const im = new Image();
          im.onload = () => resolve(im);
          im.onerror = () => reject(new Error("Failed to decode embedded JPEG"));
          im.src = url;
        });
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        switch (orientation) {
          case 2:
            canvas.width = w; canvas.height = h;
            ctx.translate(w, 0); ctx.scale(-1, 1);
            break;
          case 3:
            canvas.width = w; canvas.height = h;
            ctx.translate(w, h); ctx.rotate(Math.PI);
            break;
          case 4:
            canvas.width = w; canvas.height = h;
            ctx.translate(0, h); ctx.scale(1, -1);
            break;
          case 5:
            canvas.width = h; canvas.height = w;
            ctx.rotate(0.5 * Math.PI); ctx.scale(1, -1);
            break;
          case 6:
            canvas.width = h; canvas.height = w;
            ctx.rotate(0.5 * Math.PI); ctx.translate(0, -h);
            break;
          case 7:
            canvas.width = h; canvas.height = w;
            ctx.rotate(0.5 * Math.PI); ctx.translate(w, -h); ctx.scale(-1, 1);
            break;
          case 8:
            canvas.width = h; canvas.height = w;
            ctx.rotate(-0.5 * Math.PI); ctx.translate(-w, 0);
            break;
          default:
            canvas.width = w; canvas.height = h;
            break;
        }
        ctx.drawImage(img, 0, 0);
        return await new Promise((resolve, reject) => {
          canvas.toBlob(
            (b) => (b ? resolve(b) : reject(new Error("canvas.toBlob returned null"))),
            "image/jpeg",
            0.92
          );
        });
      } finally {
        URL.revokeObjectURL(url);
      }
    }

    loadExternalScript(src) {
      return new Promise((resolve, reject) => {
        if (document.querySelector(`script[data-src="${src}"]`)) {
          if (typeof window.heic2any === "function") return resolve();
          document
            .querySelector(`script[data-src="${src}"]`)
            .addEventListener("load", () => resolve(), { once: true });
          return;
        }
        const s = document.createElement("script");
        s.src = src;
        s.async = true;
        s.dataset.src = src;
        s.onload = () => resolve();
        s.onerror = () => reject(new Error("Failed to load " + src));
        document.head.appendChild(s);
      });
    }

    async convertHEICToJPEG(file) {
      if (typeof window.heic2any !== "function") {
        await this.loadExternalScript(this.config.heic2anyCDN);
      }
      const result = await window.heic2any({ blob: file, toType: "image/jpeg", quality: 0.92 });
      const outBlob = Array.isArray(result) ? result[0] : result;
      const newName = (file.name || "photo.heic").replace(/\.(heic|heif)$/i, ".jpg");
      return new File([outBlob], newName, { type: "image/jpeg" });
    }

    async rotateFile90CW(file) {
      // Physically rotate a browser-renderable image (jpg/png/webp/etc) 90 deg
      // clockwise via canvas. Returns a new File in JPEG format.
      const url = URL.createObjectURL(file);
      try {
        const img = await new Promise((resolve, reject) => {
          const im = new Image();
          im.onload = () => resolve(im);
          im.onerror = () => reject(new Error("Image failed to decode for rotation"));
          im.src = url;
        });
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        if (!w || !h) throw new Error("Image has no natural dimensions");
        const canvas = document.createElement("canvas");
        canvas.width = h;
        canvas.height = w;
        const ctx = canvas.getContext("2d");
        ctx.translate(h, 0);
        ctx.rotate(Math.PI / 2);
        ctx.drawImage(img, 0, 0);
        const blob = await new Promise((resolve, reject) => {
          canvas.toBlob(
            (b) => (b ? resolve(b) : reject(new Error("canvas.toBlob returned null"))),
            "image/jpeg",
            0.92
          );
        });
        const newName = (file.name || "image.jpg").replace(/(\.[^.]+)?$/, ".jpg");
        return new File([blob], newName, { type: "image/jpeg" });
      } finally {
        URL.revokeObjectURL(url);
      }
    }

    async rotateDataUrl90CW(dataUrl) {
      // Same rotation as rotateFile90CW, but for an already-rendered raster
      // data URL instead of a File. PDF/TIFF originals can't be decoded by an
      // <img> at all (see handleRotateClick), so for those we rotate the
      // pdfCanvasUrl/tiffDataUrl preview raster that createCanvasForPDF /
      // createCanvasForTIFF already rendered on upload -- the same raster
      // recropImage() uses. Returns a new PNG data URL.
      const img = await new Promise((resolve, reject) => {
        const im = new Image();
        im.onload = () => resolve(im);
        im.onerror = () => reject(new Error("Image failed to decode for rotation"));
        im.src = dataUrl;
      });
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      if (!w || !h) throw new Error("Image has no natural dimensions");
      const canvas = document.createElement("canvas");
      canvas.width = h;
      canvas.height = w;
      const ctx = canvas.getContext("2d");
      ctx.translate(h, 0);
      ctx.rotate(Math.PI / 2);
      ctx.drawImage(img, 0, 0);
      return canvas.toDataURL("image/png");
    }

    async acceptIncomingFile(rawFile) {
      if (!rawFile) return null;
      if (this.isVideoFile(rawFile)) {
        alert(
          "We can't print video files as wallpaper. Please upload a photo or image (JPG, PNG, HEIC, etc.) instead."
        );
        return null;
      }
      if (!this.validateFile(rawFile)) return null;
      if (this.isDNGFile(rawFile)) {
        try {
          this.updateUploadStatus?.("Extracting preview from RAW photo (DNG)...");
          return await this.extractDNGPreview(rawFile);
        } catch (err) {
          console.error("DNG extraction failed:", err);
          alert(
            "We couldn't read that RAW/ProRAW file. On your iPhone, open the photo \u2192 Share \u2192 Save as File \u2192 choose JPEG, then upload the JPEG version."
          );
          return null;
        }
      }
      if (this.isHEICFile(rawFile)) {
        try {
          this.updateUploadStatus?.("Converting iPhone photo (HEIC) to JPEG...");
          return await this.convertHEICToJPEG(rawFile);
        } catch (err) {
          console.error("HEIC conversion failed:", err);
          alert(
            "We couldn't convert that HEIC photo. Try exporting it as JPEG from your iPhone (Settings \u2192 Camera \u2192 Formats \u2192 Most Compatible) and upload again."
          );
          return null;
        }
      }
      return rawFile;
    }

    calculateImageQuality(dataUrl) {
      const img = new Image();
      img.onload = () => {
        this.calculateImageQualityFromImg(img);
      };
      img.src = dataUrl;
    }

    calculateImageQualityFromImg(img) {
      // Get output dimensions based on mode
      let totalWidthInches, totalHeightInches;
      let imagePixelWidth, imagePixelHeight;

      if (this.state.mode === "pattern") {
        // For pattern mode: DPI is based on pattern tile size
        totalWidthInches = this.state.patternSizeInches;
        totalHeightInches = this.state.patternSizeInches;
        imagePixelWidth = img.naturalWidth;
        imagePixelHeight = img.naturalHeight;

        // console.log("Pattern mode DPI calc:", {
        //   patternSize: this.configurator.state.patternSizeInches,
        //   imageWidth: imagePixelWidth,
        //   imageHeight: imagePixelHeight,
        // });
      } else {
        // For mural mode: DPI is based on wall dimensions
        totalWidthInches = this.state.dimensions.widthFeet * 12 + this.state.dimensions.widthInches || 48;
        totalHeightInches = this.state.dimensions.heightFeet * 12 + this.state.dimensions.heightInches || 36;

        // Check if we have cropper data (after cropping)
        if (this.state.cropperData && this.state.cropperData.width && this.state.cropperData.height) {
          // Use cropped dimensions
          imagePixelWidth = Math.round(this.state.cropperData.width);
          imagePixelHeight = Math.round(this.state.cropperData.height);

          // console.log("Using cropped dimensions for DPI:", {
          //   cropWidth: imagePixelWidth,
          //   cropHeight: imagePixelHeight,
          //   wallWidth: totalWidthInches,
          //   wallHeight: totalHeightInches,
          // });
        } else {
          // Use original image dimensions
          imagePixelWidth = img.naturalWidth;
          imagePixelHeight = img.naturalHeight;
        }
      }

      // Calculate DPI for width and height
      const dpiWidth = imagePixelWidth / totalWidthInches;
      const dpiHeight = imagePixelHeight / totalHeightInches;

      // Use the lower DPI (worst case) for quality assessment
      const minDPI = Math.min(dpiWidth, dpiHeight);
      const avgDPI = Math.round((dpiWidth + dpiHeight) / 2);

      // Store the average but assess quality on minimum
      this.state.imageDPI = avgDPI;

      // Determine quality based on the minimum DPI (weakest dimension)
      if (minDPI >= 300) {
        this.state.imageQuality = "Excellent";
      } else if (minDPI >= 150) {
        this.state.imageQuality = "Good";
      } else if (minDPI >= 72) {
        this.state.imageQuality = "Fair";
      } else {
        this.state.imageQuality = "Poor";
      }

      console.log("DPI Calculation:", {
        mode: this.state.mode,
        imagePixels: `${imagePixelWidth} \u00d7 ${imagePixelHeight}`,
        outputInches: `${totalWidthInches}" \u00d7 ${totalHeightInches}"`,
        dpiWidth: Math.round(dpiWidth),
        dpiHeight: Math.round(dpiHeight),
        avgDPI: avgDPI,
        minDPI: Math.round(minDPI),
        quality: this.state.imageQuality,
      });

      this.updateQualityDisplay();
    }

    updateQualityDisplay() {
      if (this.elements.qualityDPI) {
        this.elements.qualityDPI.textContent = this.state.imageDPI || "0";
      }

      if (this.elements.qualityScore) {
        this.elements.qualityScore.textContent = this.state.imageQuality;

        // Update color based on quality
        this.elements.qualityScore.className = "quality-value";
        if (this.state.imageQuality !== "No Image") {
          this.elements.qualityScore.classList.add(this.state.imageQuality.toLowerCase());
        }
      }
    }

    validateBeforeCart() {
      if (this.state.squareFootage <= 0) {
        alert("Please enter your wall dimensions.");
        return false;
      }

      if (!this.state.selectedMaterial) {
        alert("Please select a material.");
        return false;
      }

      if (!this.state.hasPreExisting && !this.state.originalFile) {
        alert("Please upload an image.");
        return false;
      }

      return true;
    }

    formatDimensions() {
      const { widthFeet, widthInches, heightFeet, heightInches } = this.state.dimensions;

      let widthStr = widthFeet > 0 ? `${widthFeet}'${widthInches.toFixed(1)}"` : `${widthInches.toFixed(1)}"`;
      let heightStr = heightFeet > 0 ? `${heightFeet}'${heightInches.toFixed(1)}"` : `${heightInches.toFixed(1)}"`;

      return `${widthStr} \u00d7 ${heightStr}`;
    }

    prepareUIForPatternMode() {
      this.cancelCrop({ preserveUploadedImage: true });
      this.elements.uploadZone.style.display = "none";
      this.elements.previewArea.style.display = "flex";
      this.elements.previewImage.style.display = "none";
      // Panels/Ruler cards stay visible in Pattern mode -- matches Mural layout.
      if (this.elements.patternCanvas) this.elements.patternCanvas.style.display = "block";
      if (this.elements.panelOverlay) this.elements.panelOverlay.style.display = "none";
      if (this.elements.recropBtn) this.elements.recropBtn.style.display = "none";
      // Slider lives in .tools-bar (always visible) -- enable it instead of revealing it
      this.elements.patternSettings?.classList.remove("is-disabled");
      if (this.elements.patternSizeSlider) this.elements.patternSizeSlider.disabled = false;
      // Show zoom controls for pattern mode
      const zoomControls = document.querySelector(".canvas-zoom-controls");
      if (zoomControls && this.state.originalFile) zoomControls.style.display = "block";
      // // Update mode toggle button to active state
      // if (this.elements.modePatternBtn) {
      //   this.elements.modePatternBtn.classList.add("active");
      //   this.elements.modePatternBtn.classList.add("utility-btn--active");
      // }
    }

    prepareUIForSingleMode() {
      // Slider stays visible in .tools-bar -- grey it out instead of hiding it
      this.elements.patternSettings?.classList.add("is-disabled");
      if (this.elements.patternSizeSlider) this.elements.patternSizeSlider.disabled = true;
      this.elements.previewArea.style.display = "none";
      if (this.elements.patternCanvas) this.elements.patternCanvas.style.display = "none";
      // Hide zoom controls for single mode
      const zoomControls = document.querySelector(".canvas-zoom-controls");
      if (zoomControls) zoomControls.style.display = "none";

      // Panels/Ruler cards are always visible regardless of mode -- no toggling here.

      if (this.elements.recropBtn && !this.data.force_pattern_mode)
        this.elements.recropBtn.style.display = "inline-block";
    }

    toggleFeature(feature) {
      this.state[feature] = !this.state[feature];
      const isActive = this.state[feature];

      // Update button text and state -- sync desktop card button AND mobile toolbar button
      const buttonMap = {
        showPanelDivisions: [this.elements.togglePanelDivisions, this.elements.mobileTogglePanelDivisions],
        showRuler: [this.elements.toggleRuler, this.elements.mobileToggleRuler],
      };
      (buttonMap[feature] || []).forEach((btn) => {
        if (!btn) return;
        btn.classList.toggle("active", isActive);
        if (btn.dataset.activeText) {
          btn.textContent = isActive ? btn.dataset.activeText : btn.dataset.inactiveText;
        }
        if (btn.hasAttribute("aria-pressed")) {
          btn.setAttribute("aria-pressed", String(isActive));
        }
      });

      // Apply the toggle
      switch (feature) {
        case "showRuler":
          if (isActive) {
            this.CropperController.drawRulers();
          } else {
            const rH = this.CropperController.elements.rulerH;
            const rV = this.CropperController.elements.rulerV;
            if (rH) rH.style.display = "none";
            if (rV) rV.style.display = "none";
          }
          break;
        case "showPanelDivisions":
          if (this.state.mode === "pattern") {
            this.PreviewController.updatePatternPreview();
          }
          this.CropperController.drawPanelLines();
          break;
      }
    }

    // ---- iOS canvas limits (2026-09-17) ----
    // iPhone/iPad (every iOS browser is WebKit). iPadOS reports a Mac UA, so
    // also accept "Macintosh" with touch.
    isAppleMobileWebKit() {
      const ua = navigator.userAgent || "";
      return /iP(hone|ad|od)/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
    }

    // Largest canvas area to ask for. WebKit caps a canvas at 4096x4096
    // (16,777,216 px) on iOS 17 and earlier; iOS 18 raised it to 8192x8192,
    // but the older cap is kept for every iOS device -- version sniffing is
    // unreliable on iPadOS, and a 16.7 MP preview is plenty for its job while
    // using a quarter of the memory. Desktop keeps the browser maximum.
    maxSafeCanvasArea() {
      return this.isAppleMobileWebKit() ? 16777216 : 268435456;
    }

    // True if at least one sampled pixel is non-transparent. A canvas the
    // browser couldn't back comes out fully transparent (or throws on read).
    canvasHasContent(canvas) {
      try {
        const probe = document.createElement("canvas");
        probe.width = 16;
        probe.height = 16;
        const pctx = probe.getContext("2d");
        if (!pctx) return false;
        pctx.drawImage(canvas, 0, 0, 16, 16);
        const data = pctx.getImageData(0, 0, 16, 16).data;
        for (let i = 3; i < data.length; i += 4) if (data[i] > 0) return true;
        return false;
      } catch (e) {
        return false;
      }
    }

    releaseCanvas(canvas) {
      // Shrinking first makes Safari give the memory back promptly.
      canvas.width = 1;
      canvas.height = 1;
      canvas.width = 0;
      canvas.height = 0;
    }

    // Draws the (mirrored / B&W) working image into a PNG File. Same
    // transform order as before; full size unless the device limit needs a
    // smaller copy; retries at half size (down to a 1024 px long side) if the
    // browser hands back an unusable canvas.
    async renderPreviewFile(img) {
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      if (!w || !h) throw new Error("Failed to load image for processing");
      const fileName = "PREVIEW_" + (this.state.processedFile?.name ?? this.state.originalFile?.name);
      let scale = Math.min(1, Math.sqrt(this.maxSafeCanvasArea() / (w * h)));
      for (;;) {
        const cw = Math.max(1, Math.floor(w * scale));
        const ch = Math.max(1, Math.floor(h * scale));
        const canvas = document.createElement("canvas");
        canvas.width = cw;
        canvas.height = ch;
        let blob = null;
        try {
          const ctx = canvas.getContext("2d");
          if (ctx) {
            ctx.save();
            if (this.state.options.blackAndWhite) {
              ctx.filter = "grayscale(100%)";
            }
            if (this.state.options.mirror) {
              ctx.translate(canvas.width, 0);
              ctx.scale(-1, 1);
            }
            if (scale === 1) ctx.drawImage(img, 0, 0);
            else ctx.drawImage(img, 0, 0, cw, ch);
            ctx.restore();
            if (this.canvasHasContent(canvas)) {
              blob = await new Promise((r) => canvas.toBlob(r, "image/png", 0.95));
            }
          }
        } catch (e) {
          console.warn("Preview render failed at", cw, "x", ch, e);
        } finally {
          this.releaseCanvas(canvas);
        }
        if (blob && blob.size > 0) {
          if (scale < 1) console.log("Preview scaled for this device:", cw, "x", ch, "from", w, "x", h);
          return new File([blob], fileName, { type: "image/png" });
        }
        if (Math.max(cw, ch) <= 1024) break;
        scale /= 2;
      }
      throw new Error("Failed to generate blob from canvas");
    }

    // This should be the only method that generates the final processed file
    generateProcessedPreview() {
      console.log("LOG", this.state);
      const canvas = this.elements.patternCanvas;
      if (canvas.checkVisibility()) {
        return new Promise((resolve, reject) => {
          canvas.toBlob(
            (blob) => {
              if (blob) {
                const fileName = "PREVIEW_" + (this.state.processedFile?.name ?? this.state.originalFile?.name);
                const file = new File([blob], fileName, { type: "image/png" });
                resolve(file);
              }
              reject(new Error("Failed to generate blob from canvas"));
            },
            "image/png",
            0.95,
          );
        });
      }

      return new Promise((resolve, reject) => {
        const img = new Image();

        // iOS-safe preview (2026-09-17). This used to draw at the photo's full
        // size, and iOS Safari <= 17 can't hold a canvas over 16,777,216 px
        // (4096x4096): a normal 24 MP / 48 MP iPhone photo drew nothing,
        // toBlob() gave null, and checkout stopped at "Upload failed - click
        // to retry" after the original had already uploaded. See
        // renderPreviewFile(). Only this preview changes: the original file
        // still uploads untouched, and _Crop_Data / _Original Image
        // Width/Height come from the cropper, not from this canvas.
        img.onload = async () => {
          try {
            resolve(await this.renderPreviewFile(img));
          } catch (e) {
            reject(e);
          }
        };

        img.onerror = (err) => {
          reject(new Error("Failed to load image for processing", err));
        };

        // Determine image source from state.
        // A PDF/TIFF original cannot be decoded by an <img>, so prefer the raster
        // the preview controller already rendered from it. processedFile only
        // exists once a crop has been applied -- without this, adding an uncropped
        // PDF to the cart fails with "Failed to load image for processing", leaves
        // _Preview_Image_S3_Key empty and trips the upload gate.
        const renderedRaster = this.state.pdfCanvasUrl ?? this.state.tiffDataUrl;
        if (this.state.processedFile) {
          img.src = URL.createObjectURL(this.state.processedFile);
        } else if (renderedRaster) {
          img.src = renderedRaster;
        } else if (this.state.originalFile) {
          img.src = URL.createObjectURL(this.state.originalFile);
        } else if (this.elements.previewImage?.src) {
          img.src = this.elements.previewImage.src;
        } else {
          reject(new Error("No image source available"));
        }
      });
    }

    resetOptions() {
      this.state.options.mirror = false;
      this.state.options.blackAndWhite = false;

      this.elements.modePatternBtn?.classList.remove("active");
      this.elements.modePatternBtn?.classList.remove("utility-btn--active");
      this.state.mode = "";

      this.elements.mirrorBtn?.classList.remove("active");
      this.elements.bwBtn?.classList.remove("active");
      if (this.elements.mirrorInput) this.elements.mirrorInput.value = "0";
      if (this.elements.bwInput) this.elements.bwInput.value = "0";
    }

    dataURLtoBlob(dataURL) {
      const arr = dataURL.split(",");
      const mime = arr[0].match(/:(.*?);/)[1];
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new Blob([u8arr], { type: mime });
    }
    // ==== HELPERS - END ====

    // ==== EVENT HANDLERS - START ====

    toggleOption(option) {
      this.state.options[option] = !this.state.options[option];

      const btn = option === "mirror" ? this.elements.mirrorBtn : this.elements.bwBtn;
      const input = option === "mirror" ? this.elements.mirrorInput : this.elements.bwInput;

      console.log(`Toggle ${option}:`, this.state.options[option]); // Debug log

      if (this.state.options[option]) {
        btn.classList.add("active");
        input.value = "1";
      } else {
        btn.classList.remove("active");
        input.value = "0";
      }

      EventBus.emit(OPTIONS_UPDATED, { options: this.state.options });
    }

    selectMaterial(e) {
      const selectedOption = e.target.options[e.target.selectedIndex];
      if (selectedOption.value) {
        this.state.selectedMaterial = {
          id: selectedOption.value,
          name: selectedOption.textContent,
          price: parseFloat(selectedOption.dataset.price) || 0,
        };
        this.state.selectedVariantId = selectedOption.value;
      } else {
        this.state.selectedMaterial = null;
        this.state.selectedVariantId = "";
      }

      EventBus.emit(MATERIAL_SELECTED, {});

      this.updateMaterialDisplay();
      this.calculatePrice();
      this.validateForm();
    }

    applyCrop() {
      if (!this.state.cropper) return;

      this.state.cropperData = this.state.cropper.getData();

      const canvas = this.state.cropper.getCroppedCanvas();
      canvas.toBlob(async (blob) => {
        // Create a new file from the blob for preview only
        const croppedFile = new File([blob], this.state.originalFile.name, {
          type: this.state.originalFile.type,
        });

        // Store the cropped version separately
        // this.state.croppedImage = croppedFile;
        // this.state.uploadedImage = croppedFile; // Current displayed image
        // this.state.hasCroppedImage = true; // Mark as cropped
        this.state.processedFile = croppedFile;
        // Note: originalFile remains unchanged for pattern use

        // Reset S3 data since image changed
        this.state.originalImageS3Url = null;
        this.state.originalImageS3Key = null;
        this.state.previewImageS3Url = null;
        this.state.previewImageS3Key = null;

        // Hide cropping, show preview
        this.elements.croppingArea.style.display = "none";

        EventBus.emit(IMAGE_CROPPED, { file: croppedFile });

        // Update summary. On force-pattern / force-mural pages show the product
        // title; on the customizer use the cropped file's name with optional
        // pattern-size suffix.
        if (this.elements.summaryImage) {
          const forceMode = this.data.force_pattern_mode || this.data.force_mural_mode;
          if (forceMode) {
            this.elements.summaryImage.textContent = this.data.product?.title || "Image";
          } else {
            const nameWithoutExt = Utils.getFileNameWithoutExtension(croppedFile.name);
            let imageText = nameWithoutExt;
            if (this.state.mode === "pattern") {
              imageText += ` (Pattern ${this.state.patternSizeInches}")`;
            }
            this.elements.summaryImage.textContent = imageText;
          }
        }

        this.state.cropper.destroy();
        this.state.cropper = null;

        this.validateForm();
        this.calculateImageQuality(this.elements.cropImage.src);
        this.updateUploadStatus("Image cropped - ready to add to cart");
      });
    }

    recropImage() {
      if (this.data.force_pattern_mode) return;

      // Use original file if available, otherwise use uploaded image
      const fileToRecrop = this.state.originalFile || this.state.processedFile;
      if (!fileToRecrop) return;

      // If in pattern mode, switch back to single mode
      // if (this.state.mode === "pattern") {
      //   this.configurator.setImageMode("single"); // Use setImageMode to properly update all UI
      // }

      // Hide preview
      this.elements.previewArea.style.display = "none";
      this.elements.uploadZone.style.display = "none";
      // Reset canvas if visible
      if (this.elements.patternCanvas) this.elements.patternCanvas.style.display = "none";

      if (fileToRecrop.type === "application/pdf") {
        this.CropperController.showCroppingInterface({ dataUrl: this.state.pdfCanvasUrl });
      } else if (fileToRecrop.type === "image/tiff") {
        this.CropperController.showCroppingInterface({ dataUrl: this.state.tiffDataUrl });
      } else {
        this.CropperController.showCroppingInterface({ file: fileToRecrop });
      }
      // This fixes issue with resizing crop box
      this.CropperController.updateCropperAspectRatio();
    }

    cancelCrop({ preserveUploadedImage = false } = {}) {
      if (this.state.cropper) {
        this.state.cropper.destroy();
        this.state.cropper = null;
      }

      this.elements.croppingArea.style.display = "none";
      this.elements.uploadZone.style.display = "flex";

      if (!preserveUploadedImage) {
        this.state.processedFile = null;
        this.state.originalFile = null;
        this.reset();
      }
    }

    async handleImageUpload(e) {
      const rawFile = e.target.files[0];
      if (rawFile) this.queueEditorLoader(rawFile);
      let file = await this.acceptIncomingFile(rawFile);
      if (file && (await this.isOverPixelLimit(file))) file = null;
      if (file) {
        this.hideFileTooLarge();
        this.state.originalFile = file;
        // Pattern mode doesn't allow file upload right??
        // Handle case: image upload with pattern mode toggled
        this.startPreviewWithLoader(() => this.setMode(this.state.mode || "single"));
      } else {
        this.hideEditorLoader();
      }
      e.target.value = "";
    }

    async addToCart() {
      if (!this.validateBeforeCart()) return;

      // Disable button and show uploading status
      this.elements.addToCartBtn.disabled = true;
      this.elements.addToCartBtn.textContent = "Processing...";

      // Upload to S3 if enabled and not already uploaded.
      // Re-entered on retry: anything that already uploaded keeps its key and
      // is skipped, so a second click only re-attempts what failed.
      if (this.S3 && (!this.state.originalImageS3Key || !this.state.previewImageS3Key)) {
        this.elements.addToCartBtn.textContent = "Uploading image...";
        if (!this.state.hasPreExisting && !this.state.originalImageS3Key) {
          try {
            // Upload the ORIGINAL file, not the edited/cropped version
            let file = this.state.originalFile;
            // The real original file always uploads verbatim -- including PDF/TIFF.
            // (2026-08-13: a swap to the on-screen preview raster used to run here for
            // PDF/TIFF, discarding vector/16-bit source and capping resolution at the
            // preview's hardcoded 144 DPI. Confirmed via a live probe that the backend's
            // presigned-upload endpoint accepts application/pdf and image/tiff contentType
            // fine, so the swap was an unneeded hedge -- removed.)

            const uploadResult = await this.S3.uploadFile(file, {
              productId: this.config.productId,
              dimensions: this.formatDimensions(),
              squareFootage: this.state.squareFootage,
              mirror: this.state.options.mirror,
              blackAndWhite: this.state.options.blackAndWhite,
            });
            console.log("Original image uploaded to S3:", uploadResult);
            this.state.originalImageS3Url = await this.S3.getDownloadUrl(uploadResult?.key);
            this.state.originalImageS3Key = uploadResult?.key;
          } catch (error) {
            console.error("Original image processing failed:", error);
          }
        }

        if (!this.state.previewImageS3Key) {
          try {
            const file = (await this.generateProcessedPreview()) ?? this.state.originalFile;
            // window.open(URL.createObjectURL(file), "_blank"); // DEBUGGING
            // throw new Error("Debugging - remove this line to enable preview upload");
            const uploadResult = await this.S3.uploadFile(file, {
              productId: this.config.productId,
              dimensions: this.formatDimensions(),
              squareFootage: this.state.squareFootage,
              mirror: this.state.options.mirror,
              blackAndWhite: this.state.options.blackAndWhite,
            });
            console.log("Preview image uploaded to S3:", uploadResult);
            this.state.previewImageS3Url = await this.S3.getDownloadUrl(uploadResult?.key);
            this.state.previewImageS3Key = uploadResult?.key;
          } catch (error) {
            console.error("Preview image processing failed:", error);
          }
        }
      }

      // HARD GATE (order #1218): never let a custom item into the cart without
      // its artwork and preview safely uploaded. A failed S3 upload used to be
      // logged only to the console and the order checked out with "N/A" URLs --
      // leaving the print pipeline with no source image and no way to verify
      // the customer's rotation. Stop here instead; a retry click re-attempts
      // only the upload(s) that failed.
      const missingOriginalUpload = !this.state.hasPreExisting && !this.state.originalImageS3Key;
      const missingPreviewUpload = !this.state.previewImageS3Key;
      if (missingOriginalUpload || missingPreviewUpload) {
        this.elements.addToCartBtn.disabled = false;
        this.elements.addToCartBtn.textContent = "Upload failed - click to retry";
        alert(
          "We couldn't finish uploading your image. Nothing was added to your cart.\n\n" +
            "Please check your internet connection and press the button again to retry."
        );
        return;
      }

      // Quantity is square footage rounded up
      let quantity = Math.ceil(this.state.squareFootage);
      // Enforce $120 minimum service charge by bumping billable whole-sqft units
      const _ppsf = parseFloat(this.state.selectedMaterial.price) || 0;
      if (_ppsf > 0) {
        const minQty = Math.ceil(120 / _ppsf);
        if (quantity < minQty) quantity = minQty;
      }

      // Debug log options state
      // console.log("Options state before cart:", {
      //   mirror: this.state.options.mirror,
      //   blackAndWhite: this.state.options.blackAndWhite,
      // });

      // Calculate pattern metadata if in pattern mode
      const totalWidthInches = this.state.dimensions.widthFeet * 12 + this.state.dimensions.widthInches;
      const totalHeightInches = this.state.dimensions.heightFeet * 12 + this.state.dimensions.heightInches;

      let patternMetadata = {};
      if (this.state.imageMode === "pattern") {
        // Pattern size is now directly in inches
        const patternSizeInches = this.state.patternSizeInches;
        const tilesHorizontal = Math.ceil(totalWidthInches / patternSizeInches);
        const tilesVertical = Math.ceil(totalHeightInches / patternSizeInches);

        patternMetadata = {
          "_Pattern Size (inches)": patternSizeInches,
          "_Tiles Horizontal": tilesHorizontal,
          "_Tiles Vertical": tilesVertical,
          "_Total Tiles": tilesHorizontal * tilesVertical,
        };
      }

      // Prepare cropper metadata if in single mode
      let cropperMetadata = {};
      if (this.state.cropperData) {
        cropperMetadata = {
          "_Crop X": Math.round(this.state.cropperData.x),
          "_Crop Y": Math.round(this.state.cropperData.y),
          "_Crop Width": Math.round(this.state.cropperData.width),
          "_Crop Height": Math.round(this.state.cropperData.height),
          "_Crop Rotate": this.state.cropperData.rotate || 0,
          "_Crop ScaleX": this.state.cropperData.scaleX || 1,
          "_Crop ScaleY": this.state.cropperData.scaleY || 1,
        };
      }
      // Add original image dimensions
      if (this.state.originalImageDimensions) {
        cropperMetadata["_Original Image Width"] = this.state.originalImageDimensions.width;
        cropperMetadata["_Original Image Height"] = this.state.originalImageDimensions.height;
        cropperMetadata["_Original Aspect Ratio"] = (
          this.state.originalImageDimensions.width / this.state.originalImageDimensions.height
        ).toFixed(3);
      }

      // Prepare line item properties
      const properties = {
        "Image Name": this.state.originalFile ? this.state.originalFile.name : this.state.processedFile.name,
        "Image Mode": this.state.mode === "pattern" ? "Pattern/Tile" : "Single Image",
        "Black & White": this.state.options.blackAndWhite ? "Yes" : "No",
        Dimensions: this.formatDimensions(),
        Mirror: this.state.options.mirror ? "Yes" : "No",
        _Square_Footage: this.state.squareFootage.toFixed(2),
        _Original_File_Type: this.state.originalFile ? this.state.originalFile.type : "unknown",
        _Is_Pre_Existing: this.state.hasPreExisting ? "Yes" : "No",
        _Pre_Existing_URL: this.state.preExistingUrl ? `https:${this.state.preExistingUrl}` : "N/A",
        _Original_Image_URL: this.state.originalImageS3Url || "N/A",
        _Original_Image_S3_Key: this.state.originalImageS3Key || "N/A",
        _Preview_Image_S3_URL: this.state.previewImageS3Url || "N/A",
        _Preview_Image_S3_Key: this.state.previewImageS3Key || "N/A",
        // _pdf_generation_info: JSON.stringify({
        //   sourceKey: this.state.originalImageS3Key,
        //   dimensions: this.state.dimensions,
        //   mode: this.state.imageMode,
        //   patternSizeInches: this.state.patternSizeInches,
        //   cropData: this.state.cropperData ?? this.state.cropper?.getData(),
        //   options: this.state.options,
        // }),
        _Pattern_Size_Inches: this.state.patternSizeInches,
        _DPI_Assessment: this.state.imageQuality,
        _Crop_Data: this.state.cropperData ?? this.state.cropper?.getData(),
        _get_crop_box_data: this.state.cropper?.getCropBoxData(),
        // Was this.state.cropper?.getCroppedCanvas(): a full-resolution copy of
        // the crop (up to ~200 MB on a 48 MP photo -- a crash risk on iPhones)
        // that JSON serialisation always turned into {}. Same value, no canvas.
        _get_cropped_canvas: this.state.cropper ? {} : undefined,
        ...patternMetadata,
        ...cropperMetadata,
      };
      const guestCustomerId = sessionStorage.getItem("guest_customer_id");
      if (guestCustomerId) properties["_guest_customer_id"] = guestCustomerId;

      if (quantity > Math.ceil(this.state.squareFootage)) {
        properties["_Minimum_Service_Charge_Applied"] = "Yes ($120 minimum)";
      }

      const cartData = {
        id: this.state.selectedMaterial.id,
        quantity: quantity,
        properties: properties,
      };

      this.elements.addToCartBtn.textContent = "Adding to cart...";

      try {
        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(cartData),
        });

        if (response.ok) {
          // Clear localStorage on successful cart add
          this.StorageManager.clearStateFromStorage();
          window.location.href = "/cart";
        } else {
          throw new Error("Failed to add to cart");
        }
      } catch (error) {
        console.error("Error adding to cart:", error);
        alert("Error adding to cart. Please try again.");

        this.elements.addToCartBtn.disabled = false;
        this.elements.addToCartBtn.innerHTML =
          '<svg class="cart-icon" width="20" height="20" viewBox="0 0 20 20" fill="currentColor"><path d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17M17 13v6m0 0v2m0-2h2m-2 0h-2"/></svg><span>Add To Cart</span>';
      }
    }

    handleDragOver(e) {
      e.preventDefault();
      this.elements.uploadZone.classList.add("drag-over");
    }

    async handleDrop(e) {
      e.preventDefault();
      this.elements.uploadZone.classList.remove("drag-over");

      const rawFile = e.dataTransfer.files[0];
      if (rawFile) this.queueEditorLoader(rawFile);
      let file = await this.acceptIncomingFile(rawFile);
      if (file && (await this.isOverPixelLimit(file))) file = null;
      if (file) {
        this.hideFileTooLarge();
        this.state.originalFile = file;
        this.startPreviewWithLoader(() => this.setMode(this.state.mode || "single"));
      } else {
        this.hideEditorLoader();
      }
    }

    handleDragLeave(e) {
      e.preventDefault();
      this.elements.uploadZone.classList.remove("drag-over");
    }

    // ==== EVENT LISTENERS - END ====

    attachEventListeners() {
      // Dimension changes with validation
      ["widthFeet", "widthInch", "heightFeet", "heightInch"].forEach((field) => {
        this.elements[field]?.addEventListener("input", (e) => {
          // Validate and limit input values (decimals allowed, e.g. 1.5 in)
          const input = e.target;
          let value = parseFloat(input.value) || 0;

          // Apply limits based on field type
          if (field.includes("Feet")) {
            // Limit feet to max 999
            if (value > 999) {
              value = 999;
              input.value = 999;
            }
          } else if (field.includes("Inch")) {
            // Limit inches to under 12 (11.9 max keeps decimals like 11.5 valid)
            if (value >= 12) {
              value = 11.9;
              input.value = 11.9;
            }
          }

          // Prevent negative values
          if (value < 0) {
            input.value = 0;
          }

          this.calculateDimensions();
          this.updateUploadState();
          this.updateDimensionsSummary();
          this.calculatePrice();
          this.validateForm();

          // Update pattern slider range when dimensions change
          // this.updatePatternSliderRange();
          // this.updatePatternSliderConstraints();

          EventBus.emit(DIMENSIONS_UPDATED, { dimensions: this.state.dimensions });
        });

        this.elements[field]?.addEventListener("blur", (e) => {
          const input = e.target;
          const value = parseFloat(input.value);
          if (!isNaN(value)) input.value = value;
          else input.value = 0;
        });
      });

      // Material selection -- hidden <select> (drives JS state)
      this.elements.materialSelect?.addEventListener("change", (e) => this.selectMaterial(e));

      // Visible material list rows (desktop). Each click updates the hidden
      // select and fires a change event so all existing listeners run normally.
      this.querySelectorAll(".material-item").forEach((item) => {
        item.addEventListener("click", () => {
          const select = this.elements.materialSelect;
          if (!select) return;
          select.value = item.dataset.variantId;
          select.dispatchEvent(new Event("change", { bubbles: true }));
        });
      });

      // Image upload
      this.elements.imageUpload?.addEventListener("change", (e) => this.handleImageUpload(e));
      // Cropper.js dispatches "ready" on the image once the editor is usable.
      this.elements.cropImage?.addEventListener("ready", () => this.hideEditorLoader());
      // cleanUp() blanks src (fires "error" too) -- only a real load failure counts.
      this.elements.cropImage?.addEventListener("error", (e) => {
        if (e.target.getAttribute("src")) this.hideEditorLoader();
      });
      this.elements.uploadZone?.addEventListener("click", () => this.elements.imageUpload.click());

      // Drag and drop
      this.elements.uploadZone?.addEventListener("dragover", (e) => this.handleDragOver(e));
      this.elements.uploadZone?.addEventListener("dragleave", (e) => this.handleDragLeave(e));
      this.elements.uploadZone?.addEventListener("drop", (e) => this.handleDrop(e));

      this.elements.clearImageBtn?.addEventListener("click", () => this.reset());

      // Re-crop image
      this.elements.recropBtn?.addEventListener("click", () => this.recropImage());

      // Rotate image 90 deg clockwise. Physically rotates the source file via
      // canvas, then re-enters cropping mode with the rotated file -- works
      // consistently whether the user is mid-crop or viewing the preview.
      // The single Rotate 90 deg button now lives in the unified .tools-bar (below canvas).
      const handleRotateClick = async () => {
        const source = this.state.originalFile || this.state.processedFile;
        if (!source) return;
        const disableBtns = (disabled) => {
          if (this.elements.rotateBtn) this.elements.rotateBtn.disabled = disabled;
        };
        disableBtns(true);
        try {
          // PDF/TIFF can't be decoded by an <img> (rotateFile90CW's canvas
          // trick needs a browser-renderable image), so instead rotate the
          // already-rendered preview raster -- recropImage() uses this same
          // pdfCanvasUrl/tiffDataUrl for the same reason. originalFile (the
          // real PDF/TIFF bytes) is left untouched so it still uploads to S3
          // verbatim -- see the 2026-08-13 note in addToCart. Like every
          // rotate, this isn't recorded onto the original file; print prep
          // checks the uploaded preview image for orientation.
          if (source.type === "application/pdf" || source.type === "image/tiff") {
            const isPdf = source.type === "application/pdf";
            const currentRaster = isPdf ? this.state.pdfCanvasUrl : this.state.tiffDataUrl;
            if (!currentRaster) throw new Error("No rendered preview available to rotate");
            const rotatedDataUrl = await this.rotateDataUrl90CW(currentRaster);
            if (isPdf) {
              this.state.pdfCanvasUrl = rotatedDataUrl;
            } else {
              this.state.tiffDataUrl = rotatedDataUrl;
              this.state.tiffCanvasUrl = rotatedDataUrl;
            }
            if (this.state.mode === "pattern") {
              this.elements.croppingArea.style.display = "none";
              this.elements.uploadZone.style.display = "none";
              this.elements.previewArea.style.display = "flex";
              EventBus.emit(SWITCHING_MODE, { file: source, mode: "pattern" });
            } else {
              this.elements.previewArea.style.display = "none";
              this.elements.uploadZone.style.display = "none";
              if (this.elements.patternCanvas) this.elements.patternCanvas.style.display = "none";
              await this.CropperController.showCroppingInterface({ dataUrl: rotatedDataUrl });
              this.CropperController.updateCropperAspectRatio();
            }
            return;
          }

          const rotated = await this.rotateFile90CW(source);
          this.state.originalFile = rotated;
          this.state.processedFile = rotated;
          if (this.state.mode === "pattern") {
            // Pattern mode: regenerate the pattern preview with the rotated
            // file. Don't enter the cropping UI -- it's single-mode only and
            // forces the pattern slider back to its default size.
            this.elements.croppingArea.style.display = "none";
            this.elements.uploadZone.style.display = "none";
            this.elements.previewArea.style.display = "flex";
            EventBus.emit(SWITCHING_MODE, { file: rotated, mode: "pattern" });
          } else {
            this.elements.previewArea.style.display = "none";
            this.elements.uploadZone.style.display = "none";
            if (this.elements.patternCanvas) this.elements.patternCanvas.style.display = "none";
            await this.CropperController.showCroppingInterface({ file: rotated });
            this.CropperController.updateCropperAspectRatio();
          }
        } catch (err) {
          console.error("[rotate] failed:", err);
          alert("We couldn't rotate this image. Try uploading it as JPEG/PNG.");
        } finally {
          disableBtns(false);
        }
      };
      this.elements.rotateBtn?.addEventListener("click", handleRotateClick);

      // Mode Toggle - Single button that toggles between pattern and mural.
      // Panels/Ruler cards stay visible in both modes -- matches Mural layout.
      this.elements.modePatternBtn?.addEventListener("click", () => {
        const newMode = this.state.mode === "pattern" ? "single" : "pattern";
        this.elements.modePatternBtn.classList.toggle("active");
        this.elements.modePatternBtn.classList.toggle("utility-btn--active");
        this.setMode(newMode);
      });

      // Options
      this.elements.mirrorBtn?.addEventListener("click", () => this.toggleOption("mirror"));
      this.elements.bwBtn?.addEventListener("click", () => this.toggleOption("blackAndWhite"));
      this.elements.resetAllBtn?.addEventListener("click", () => this.configurator.resetAll());

      // Pattern size slider - dynamically adjust based on wall dimensions
      this.elements.patternSizeSlider?.addEventListener("input", (e) => {
        this.state.patternSizeInches = parseInt(e.target.value);
        this.updatePatternSizeDisplay();
        if (this.state.mode === "pattern") {
          this.PreviewController.updatePatternPreview();
        }
        // Update summary displays
        this.updateDimensionDisplay();
      });

      // Canvas zoom controls - DISABLED FOR NOW
      // this.initCanvasZoomControls();

      // Add to cart
      this.elements.addToCartBtn?.addEventListener("click", () => this.addToCart());

      // Toggle controls (desktop cards + mobile toolbar share the same state)
      this.elements.togglePanelDivisions?.addEventListener("click", () => this.toggleFeature("showPanelDivisions"));
      this.elements.toggleRuler?.addEventListener("click", () => this.toggleFeature("showRuler"));
      this.elements.mobileTogglePanelDivisions?.addEventListener("click", () => this.toggleFeature("showPanelDivisions"));
      this.elements.mobileToggleRuler?.addEventListener("click", () => this.toggleFeature("showRuler"));

      // // Handle window resize for responsive canvas
      // let resizeTimeout;
      // window.addEventListener("resize", () => {
      //   clearTimeout(resizeTimeout);
      //   resizeTimeout = setTimeout(() => {
      //     // Only update if we have an image and dimensions
      //     if (this.state.uploadedImage && this.state.wallArea > 0) {
      //       // console.log("Window resized - updating canvas display");
      //
      //       // Update pattern preview if in pattern mode
      //       if (this.configurator.state.imageMode === "pattern") {
      //         this.updatePatternPreview();
      //       } else {
      //         // For single image mode, just update the display
      //         this.updateSingleImageDisplay();
      //       }
      //     }
      //   }, 250); // Debounce delay of 250ms
      // });
      //
      // // Clean up when tab is closed
      // window.addEventListener("beforeunload", () => {
      //   this.clearObjectUrls();
      //   // Optionally clear session storage on tab close
      //   // this.StorageManager.clearStateFromStorage();
      // });
      //
      // // Save state periodically (every 30 seconds) while tab is active
      // setInterval(() => {
      //   if (document.visibilityState === "visible") {
      //     this.StorageManager.saveStateToStorage();
      //   }
      // }, 30000);
    }

    removeImage() {
      // this.configurator.ImageProcessor.cleanupPreviousFile();

      // this.state.originalFile = null;
      this.state.processedFile = null;

      // this.state.croppedImage = null;
      this.state.cropperData = null;
      // this.state.hasCroppedImage = false;
      // this.state.originalImageS3Url = null;
      // this.state.originalImageS3Key = null;
      // this.state.previewImageS3Url = null;
      // this.state.previewImageS3Key = null;
      // this.state.imageDPI = 0;
      // this.state.imageQuality = "No Image";
      this.state.tiffDataUrl = null;
      this.state.pdfCanvasUrl = null;

      this.resetOptions();

      // Hide zoom controls (since there's no image to zoom)
      const zoomControls = document.querySelector(".canvas-zoom-controls");
      if (zoomControls) zoomControls.style.display = "none";

      // Reset zoom state
      this.canvasZoom = 1;
      this.canvasPosition = { x: 0, y: 0 };
      // this.CanvasController.updateCanvasTransform();

      if (this.elements.croppingArea) this.elements.croppingArea.style.display = "none";

      // Show upload zone
      this.elements.previewArea.style.display = "none";
      this.elements.uploadZone.style.display = "flex";

      if (this.elements.summaryImage) {
        // Force modes reset to the product title, not "No Image", since the
        // Order Summary for pattern/mural products always carries the title.
        const forceMode = this.data.force_pattern_mode || this.data.force_mural_mode;
        this.elements.summaryImage.textContent = forceMode
          ? this.data.product?.title || "Image"
          : "No Image";
      }

      this.updateQualityDisplay();
      this.updateUploadState();
      this.validateForm();
      this.cleanUp();
    }

    /**
     * Calculate the maximum height available for .upload-canvas content,
     * based on the right column height minus siblings and gaps.
     * Used by both pattern preview and cropping interface for consistency.
     */
    getCanvasMaxHeight() {
      const rightColumn = document.querySelector(".configurator-right");
      const bottomCards = document.querySelector(".bottom-cards-container");
      const toolsBar = document.querySelector(".tools-bar");
      const leftGap = 30; // .configurator-left flex gap
      if (!rightColumn) return null;

      // The unified tools-bar (buttons + pattern slider) sits between the canvas
      // and bottom-cards in the left column. Subtract its full height so the
      // canvas shares the available vertical space with siblings.
      const toolsBarHeight = toolsBar ? toolsBar.offsetHeight : 0;

      // Cards are in a single flex row, so container height = tallest card.
      // Just read the container's offsetHeight directly.
      const siblingHeight = bottomCards ? bottomCards.offsetHeight : 0;

      const height = rightColumn.offsetHeight - toolsBarHeight - siblingHeight - leftGap;

      return height;
    }

    cleanUp() {
      this.objectUrls.forEach((url) => URL.revokeObjectURL(url));
      this.objectUrls = [];

      if (this.state.cropper) {
        this.state.cropper.destroy();
        this.state.cropper = null;
      }

      if (this.elements.previewImage) this.elements.previewImage.src = "";
      if (this.elements.cropImage) this.elements.cropImage.src = "";

      if (this.elements.patternCanvas) {
        const ctx = this.elements.patternCanvas.getContext("2d");
        if (ctx) {
          ctx.clearRect(0, 0, this.elements.patternCanvas.width, this.elements.patternCanvas.height);
          this.elements.patternCanvas.width = 1;
          this.elements.patternCanvas.height = 1;
        }
      }
      // Cleanup other canvases?

      clearInterval(this.intervalId);
    }

    reset() {
      // Destroy the old cropper BEFORE the state is replaced (2026-09-17).
      // Otherwise it stays bound to #crop-image, Cropper.js refuses to start a
      // new instance on that element, and after "Remove Image" the next upload
      // keeps showing the old photo and Add to Cart throws.
      if (this.state.cropper) {
        this.state.cropper.destroy();
        this.state.cropper = null;
      }
      this.state = { ...INIT_STATE, initialized: true };
      this.removeImage();
      this.updateUI();
      this.StorageManager.clearStateFromStorage();
    }

    disconnectedCallback() {
      this.cleanUp();
    }
  }
  customElements.define("wallpaper-configurator", WallpaperConfigurator);
}

window.addEventListener("beforeunload", () => {});
